import {
  Container,
  Grid,
  Stack,
  Typography,
  Box,
  Avatar,
} from "@mui/material";
import React, { useEffect } from "react";
import { themeBlue, themeOrange } from "../../../utils/colorTheme";
import { useDispatch, useSelector } from "react-redux";
import { imgUrl } from "../../../utils/imageUrl";
import {
  getVendorDataAsync,
  JobGetByVendorAsync,
} from "../../../services/services";
import BarChart from "../../../component/Graphs/BarGraph/BarGraph";
import { useNavigate } from "react-router-dom";
import { MdWork, MdTrendingUp, MdAccessTime, MdCheckCircle, MdCancel, MdAttachMoney } from "react-icons/md";
import { FaCalendarCheck, FaChartLine } from "react-icons/fa";

const DashboardHome = () => {
  const { user } = useSelector((state) => state.userAuth);
  const { get_job_by_vendor_data, get_vendor_data } = useSelector(
    (state) => state.serviceManage
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    const isEmailVerified = user?.email_verified_at;
    if (!isEmailVerified) {
      navigate("/vendor-check-phone");
    }
    dispatch(JobGetByVendorAsync());
    dispatch(getVendorDataAsync());
    window.scrollTo(0, 0);
  }, []);

  const COMPLETED_STATUSES = ["Completed", "Paid"];
  const PENDING_STATUSES = ["Assigned", "Pending", "In-Queue"];

  const statusCounts = Array.isArray(get_job_by_vendor_data)
    ? get_job_by_vendor_data?.reduce(
        (acc, item) => {
          if (COMPLETED_STATUSES.includes(item?.status)) acc.completed += 1;
          else if (item?.status === "Cancelled") acc.cancelled += 1;
          else if (PENDING_STATUSES.includes(item?.status)) acc.pending += 1;
          return acc;
        },
        { completed: 0, cancelled: 0, pending: 0 }
      )
    : { completed: 0, cancelled: 0, pending: 0 };

  const calculatedTotals = Array.isArray(get_job_by_vendor_data)
    ? get_job_by_vendor_data.reduce(
        (acc, job) => {
          const total = parseFloat(job?.total_amount || job?.service?.price || 0);
          const tax = parseFloat(job?.tax || 0);
          const net = total - tax;
          // Pro receives 75% of (total - tax); matches backend VenDashboard share calc
          const proShare = net > 0 ? net * 0.75 : 0;

          if (proShare > 0) {
            if (COMPLETED_STATUSES.includes(job?.status)) {
              acc.totalEarned += proShare;
            } else if (PENDING_STATUSES.includes(job?.status)) {
              acc.pendingAmount += proShare;
            }
          }
          return acc;
        },
        { totalEarned: 0, pendingAmount: 0 }
      )
    : { totalEarned: 0, pendingAmount: 0 };

  // Log final totals
  console.log("📊 Earnings Summary:");
  console.log("Total Earned:", calculatedTotals.totalEarned);
  console.log("Pending Amount:", calculatedTotals.pendingAmount);

  const recentJobs = Array.isArray(get_job_by_vendor_data)
    ? get_job_by_vendor_data.slice(-5).reverse()
    : [];

  const StatCard = ({ icon: Icon, title, value, subtitle, gradient, iconBg }) => (
    <Box
      sx={{
        background: gradient,
        borderRadius: "20px",
        p: { xs: 2, sm: 3 },
        height: "100%",
        position: "relative",
        overflow: "hidden",
        boxShadow: "0 10px 40px rgba(0,0,0,0.12)",
        transition: "transform 0.3s ease, box-shadow 0.3s ease",
        "&:hover": {
          transform: "translateY(-5px)",
          boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
        },
      }}
    >
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
        <Stack spacing={1}>
          <Typography sx={{ color: "rgba(255,255,255,0.8)", fontSize: { xs: "12px", sm: "14px" }, fontWeight: 500 }}>
            {title}
          </Typography>
          <Typography sx={{ color: "#fff", fontSize: { xs: "26px", sm: "32px", md: "36px" }, fontWeight: 700, lineHeight: 1.2 }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: { xs: "11px", sm: "13px" } }}>
              {subtitle}
            </Typography>
          )}
        </Stack>
        <Box
          sx={{
            width: { xs: 44, sm: 56 },
            height: { xs: 44, sm: 56 },
            borderRadius: "16px",
            backgroundColor: iconBg || "rgba(255,255,255,0.2)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Icon size={22} color="#fff" />
        </Box>
      </Stack>
      {/* Decorative circle */}
      <Box
        sx={{
          position: "absolute",
          bottom: -30,
          right: -30,
          width: 120,
          height: 120,
          borderRadius: "50%",
          backgroundColor: "rgba(255,255,255,0.1)",
        }}
      />
    </Box>
  );

  const EarningsCard = ({ title, amount, icon: Icon, color }) => (
    <Box
      sx={{
        backgroundColor: "#fff",
        borderRadius: "20px",
        p: { xs: 2, sm: 3 },
        height: "100%",
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        border: "1px solid #f0f0f0",
        transition: "transform 0.3s ease, box-shadow 0.3s ease",
        "&:hover": {
          transform: "translateY(-3px)",
          boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
        },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={2}>
        <Box
          sx={{
            width: { xs: 48, sm: 60 },
            height: { xs: 48, sm: 60 },
            borderRadius: "16px",
            background: `linear-gradient(135deg, ${color}20, ${color}10)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Icon size={24} color={color} />
        </Box>
        <Stack>
          <Typography sx={{ color: "#666", fontSize: { xs: "12px", sm: "14px" }, fontWeight: 500 }}>
            {title}
          </Typography>
          <Typography sx={{ color: "#1a1a1a", fontSize: { xs: "20px", sm: "24px", md: "28px" }, fontWeight: 700 }}>
            ${amount}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );

  const QuickStatCard = ({ title, value, icon: Icon, color }) => (
    <Box
      sx={{
        backgroundColor: "#fff",
        borderRadius: "20px",
        p: 3,
        boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
        border: "1px solid #e5e7eb",
        transition: "all 0.3s ease",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 12px 24px rgba(0,0,0,0.08)",
        },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={2.5}>
        <Box
          sx={{
            width: { xs: 48, sm: 64 },
            height: { xs: 48, sm: 64 },
            borderRadius: "16px",
            background: `${color}15`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Icon size={24} color={color} />
        </Box>
        <Stack>
          <Typography
            sx={{
              fontSize: { xs: "10px", sm: "12px" },
              color: "#9ca3af",
              fontWeight: "600",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              mb: 0.5,
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: "22px", sm: "28px", md: "36px" },
              fontWeight: "700",
              color: "#000",
              lineHeight: 1,
            }}
          >
            {value}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );

  return (
    <Box sx={{ backgroundColor: "#f8fafc", minHeight: "100vh", pb: 4 }}>
      <Container maxWidth="xl">
        {/* Welcome Header */}
        <Box sx={{ pt: 4, pb: 3 }}>
          <Stack direction="row" alignItems="center" spacing={2} mb={1}>
            <Avatar
              src={user?.avatar ? `${imgUrl}${user.avatar}` : null}
              sx={{
                width: 56,
                height: 56,
                backgroundColor: themeOrange,
                fontSize: "24px",
                fontWeight: 600,
              }}
            >
              {user?.first_name?.[0] || "V"}
            </Avatar>
            <Stack>
              <Typography sx={{ color: "#666", fontSize: { xs: "12px", sm: "14px" } }}>
                Welcome back,
              </Typography>
              <Typography sx={{ color: "#1a1a1a", fontSize: { xs: "20px", sm: "24px", md: "28px" }, fontWeight: 700 }}>
                {user?.first_name || "Pro"} {user?.last_name || ""}
              </Typography>
            </Stack>
          </Stack>
          <Typography sx={{ color: "#888", fontSize: "15px", mt: 1 }}>
            Here's what's happening with your business today.
          </Typography>
        </Box>

        {/* Main Stats Cards */}
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} sm={6} lg={3}>
            <StatCard
              icon={FaCalendarCheck}
              title="Today's Bookings"
              value={get_vendor_data?.today_bookings || 0}
              subtitle="Active jobs today"
              gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <StatCard
              icon={MdWork}
              title="Total Bookings"
              value={get_vendor_data?.total_bookings || 0}
              subtitle="All time jobs"
              gradient="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <StatCard
              icon={MdCheckCircle}
              title="Completed"
              value={statusCounts.completed}
              subtitle="Successfully done"
              gradient="linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)"
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <StatCard
              icon={MdAccessTime}
              title="Pending"
              value={statusCounts.pending}
              subtitle="Awaiting completion"
              gradient="linear-gradient(135deg, #fa709a 0%, #fee140 100%)"
            />
          </Grid>
        </Grid>

        {/* Earnings Section */}
        <Typography sx={{ fontSize: "20px", fontWeight: 600, color: "#1a1a1a", mb: 2 }}>
          Earnings Overview
        </Typography>
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} sm={6}>
            <EarningsCard
              title="Total Earned"
              amount={calculatedTotals.totalEarned.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
              icon={MdAttachMoney}
              color="#22c55e"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <EarningsCard
              title="Pending Amount"
              amount={calculatedTotals.pendingAmount.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
              icon={MdTrendingUp}
              color={themeOrange}
            />
          </Grid>
        </Grid>

        {/* Quick Stats */}
        <Typography sx={{ fontSize: "20px", fontWeight: 600, color: "#1a1a1a", mb: 2 }}>
          Job Statistics
        </Typography>
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} sm={6}>
            <QuickStatCard title="Completed" value={statusCounts.completed} icon={MdCheckCircle} color="#22c55e" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <QuickStatCard title="Cancelled" value={statusCounts.cancelled} icon={MdCancel} color="#ef4444" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <QuickStatCard title="Pending" value={statusCounts.pending} icon={MdAccessTime} color="#f59e0b" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <QuickStatCard title="Total Jobs" value={get_vendor_data?.total_bookings || 0} icon={MdWork} color="#3b82f6" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <QuickStatCard title="Today" value={get_vendor_data?.today_bookings || 0} icon={FaCalendarCheck} color="#8b5cf6" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <QuickStatCard title="Success Rate" value={`${statusCounts.completed > 0 ? Math.round((statusCounts.completed / (statusCounts.completed + statusCounts.cancelled)) * 100) : 0}%`} icon={FaChartLine} color="#14b8a6" />
          </Grid>
        </Grid>

        {/* Chart Section */}
        <Box
          sx={{
            backgroundColor: "#fff",
            borderRadius: "20px",
            p: 3,
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            border: "1px solid #f0f0f0",
          }}
        >
          <Typography sx={{ fontSize: "20px", fontWeight: 600, color: "#1a1a1a", mb: 3 }}>
            Jobs & Earnings Overview
          </Typography>
          <BarChart />
        </Box>

        {/* Recent Jobs Section */}
        {recentJobs.length > 0 && (
          <Box sx={{ mt: 4 }}>
            <Typography sx={{ fontSize: "20px", fontWeight: 600, color: "#1a1a1a", mb: 2 }}>
              Recent Jobs
            </Typography>
            <Box
              sx={{
                backgroundColor: "#fff",
                borderRadius: "20px",
                overflow: "hidden",
                boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                border: "1px solid #f0f0f0",
              }}
            >
              {recentJobs.map((job, index) => (
                <Stack
                  key={index}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    p: 2.5,
                    borderBottom: index < recentJobs.length - 1 ? "1px solid #f5f5f5" : "none",
                    transition: "background-color 0.2s ease",
                    "&:hover": {
                      backgroundColor: "#fafafa",
                    },
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Avatar
                      src={job?.user?.avatar ? `${imgUrl}${job.user.avatar}` : null}
                      sx={{ width: 44, height: 44, backgroundColor: "#e5e5e5" }}
                    >
                      {job?.user?.first_name?.[0] || "U"}
                    </Avatar>
                    <Stack>
                      <Typography sx={{ fontSize: "15px", fontWeight: 600, color: "#1a1a1a" }}>
                        {job?.user?.first_name || ""} {job?.user?.last_name || ""}
                      </Typography>
                      <Typography sx={{ fontSize: "13px", color: "#888" }}>
                        {job?.service?.name || "Service"}
                      </Typography>
                    </Stack>
                  </Stack>
                  <Box
                    sx={{
                      px: 2,
                      py: 0.5,
                      borderRadius: "20px",
                      backgroundColor:
                        job?.status === "Completed"
                          ? "#dcfce7"
                          : job?.status === "Cancelled"
                          ? "#fee2e2"
                          : job?.status === "Assigned"
                          ? "#dbeafe"
                          : "#fef3c7",
                      color:
                        job?.status === "Completed"
                          ? "#22c55e"
                          : job?.status === "Cancelled"
                          ? "#ef4444"
                          : job?.status === "Assigned"
                          ? "#3b82f6"
                          : "#f59e0b",
                      fontSize: "12px",
                      fontWeight: 600,
                    }}
                  >
                    {job?.status}
                  </Box>
                </Stack>
              ))}
            </Box>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default DashboardHome;
